import { computed, watch } from 'vue';
import { sortEventsByStartTime } from '@/js/core/utils/calendar-events';
import { calendarFiltersForApi } from '@/js/core/utils/calendar-filter-state';
import { eventsRequestKey } from '@/js/core/stores/events-list-store';

/**
 * Watches a date range and filter selection, fetches events reactively,
 * and returns the backend-filtered result list in display order.
 *
 * @param {object} eventsStore - result of useEventsListStore()
 * @param {import('vue').Ref<{start_after: string, start_before: string}>} dateRange
 * @param {import('vue').Ref<object>} selectedFilters
 * @param {boolean} delayUpdates Whether to debounce updates by 500ms e.g. to allow multiple filter changes close to
 * one another
 * @param {object|import('vue').Ref<object>} requestParams Additional request params for the Events API.
 * @param {import('vue').Ref<boolean>|null} enabled Hold fetching off while false; fetches on the
 * transition to true. Used to keep an index page that is mounted behind a full-page modal from
 * competing with it for requests.
 * @returns {{ filteredEvents: import('vue').ComputedRef, isCurrentRangeLoaded: import('vue').ComputedRef<boolean> }}
 */
export function useCalendarEvents(
  eventsStore,
  dateRange,
  selectedFilters,
  delayUpdates,
  requestParams = {},
  enabled = null
) {
  let updateTimer = null;

  const additionalParams = () => requestParams?.value || requestParams || {};
  const isEnabled = () => (enabled ? enabled.value !== false : true);

  const requestPayload = computed(() => ({
    ...additionalParams(),
    date_ranges: dateRange.value,
    per_page: 100,
    filters: calendarFiltersForApi(selectedFilters.value),
  }));

  const handleUpdate = () => {
    eventsStore.fetchEvents(requestPayload.value, 'calendar', null);
  };

  const filteredEvents = computed(() => {
    return sortEventsByStartTime(eventsStore.events);
  });

  // False while the store still holds a previous range's events, letting callers keep showing
  // what is already on screen rather than re-filtering stale records against the new range.
  const isCurrentRangeLoaded = computed(
    () => eventsStore.loadedRequestKey === eventsRequestKey(requestPayload.value)
  );

  watch(
    [dateRange, selectedFilters, () => isEnabled()],
    () => {
      if (!isEnabled()) {
        return;
      }

      eventsStore.isLoading = true;

      if (delayUpdates) {
        if (updateTimer) {
          clearTimeout(updateTimer);
          updateTimer = null;
        }

        updateTimer = setTimeout(handleUpdate, 500);
      } else {
        handleUpdate();
      }
    },
    { deep: true, immediate: true }
  );

  return { filteredEvents, isCurrentRangeLoaded };
}
